import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ShieldCheck, RefreshCw, Search, Crown, ChevronLeft, X, Save, Loader2,
  History as HistoryIcon, AlertTriangle, Check,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminRbacMe, adminRbacRoles, adminRbacPermissions,
  adminRbacUsers, adminRbacPatchUser, adminRbacAuditLog,
} from "../../lib/api";

/**
 * AdminUsersPanel — Owner-only RBAC management surface.
 *
 * Surfaces:
 *   • List of OAuth-backed user rows with role + effective permissions
 *   • Per-user drawer: appoint role, apply template, grant/revoke perms,
 *     suspend (Owner is locked — controls auto-disable)
 *   • RBAC audit log
 *
 * The page itself checks `is_owner` from /admin/rbac/me. Non-Owners
 * see a friendly "Owner-only" notice instead of the controls. The
 * backend also enforces Owner-only on every write — the UI just stops
 * non-Owners from ever issuing the request.
 */

const ROLE_META = {
  owner:           { label: "OWNER",           color: "#E0A526", rank: 100 },
  administrator:   { label: "ADMINISTRATOR",   color: "#D92D20", rank: 80  },
  editor:          { label: "EDITOR",          color: "#5DD4B3", rank: 60  },
  researcher:      { label: "RESEARCHER",      color: "#9FA8D4", rank: 50  },
  moderator:       { label: "MODERATOR",       color: "#CBB892", rank: 40  },
  founding_member: { label: "FOUNDING MEMBER", color: "#E0A526", rank: 30  },
  member:          { label: "MEMBER",          color: "#5DD4B3", rank: 20  },
  subscriber:      { label: "SUBSCRIBER",      color: "#9FA8D4", rank: 10  },
  user:            { label: "USER",            color: "#757065", rank: 5   },
  anonymous:       { label: "ANONYMOUS",       color: "#454038", rank: 0   },
};

const APPOINT_ROLES = ["administrator", "editor", "researcher", "moderator"];

export const AdminUsersPanel = ({ token, userToken = null }) => {
  const [me, setMe] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);
  const [tab, setTab] = useState("users"); // users | audit

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [meR, ur] = await Promise.all([
        adminRbacMe(token, userToken),
        adminRbacUsers(token, { q: q || undefined, limit: 200 }, userToken),
      ]);
      setMe(meR);
      setUsers(ur.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load users");
    } finally { setLoading(false); }
  }, [token, userToken, q]);

  useEffect(() => { load(); }, [load]);

  const isOwnerActor = !!me?.is_owner;

  return (
    <div data-testid="admin-users-panel">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <ShieldCheck size={13} /> // PERMISSIONS · RBAC
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#E4DFD3]">
            Users &amp; Permissions
          </h1>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            Appoint Administrators, Editors, Researchers, Moderators. Grant
            permissions à la carte or via templates. The Owner is permanent
            and cannot be edited from this surface.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div
            className="border border-[#E0A526]/40 bg-[#1a1408] px-2 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] text-[#E0A526] inline-flex items-center gap-1.5"
            data-testid="admin-users-owner-banner"
            title={`Owner email: ${me?.owner_email_configured || "—"}`}
          >
            <Crown size={11} /> Owner · {me?.owner_email_configured || "—"}
          </div>
        </div>
      </div>

      <div className="flex border-b border-[#2D2A26] mb-3">
        {[["users", "Users"], ["audit", "Audit Log"]].map(([k, l]) => {
          const active = tab === k;
          return (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-users-tab-${k}`}
              className={
                "px-3 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 " +
                (active
                  ? "border-[#D92D20] text-[#D92D20]"
                  : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")
              }
            >
              {l}
            </button>
          );
        })}
      </div>

      {tab === "users" && (
        <>
          {!isOwnerActor && (
            <div className="border border-[#E0A526]/40 bg-[#1a1408] p-3 mb-3 flex items-start gap-2" data-testid="admin-users-not-owner">
              <AlertTriangle size={14} className="text-[#E0A526] mt-0.5" />
              <div className="font-mono text-[11px] text-[#CBB892]">
                Owner-only surface. You can view the roster but write actions
                are disabled. To make a change, the Owner ({me?.owner_email_configured || "—"})
                must be signed in.
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="flex items-center gap-1.5 border border-[#2D2A26] focus-within:border-[#D92D20] px-2 py-1.5 bg-[#0F0F0F]">
              <Search size={12} className="text-[#757065]" />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="email or name…"
                data-testid="admin-users-search"
                className="bg-transparent outline-none font-mono text-[11px] text-[#E4DFD3] placeholder-[#757065] w-48"
              />
            </div>
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="admin-users-refresh"
              className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]"
            >
              <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
            </button>
          </div>

          <div className="border border-[#2D2A26] bg-[#131313]" data-testid="admin-users-list">
            {loading && users.length === 0 ? (
              <div className="px-3 py-10 text-center text-[#757065] font-mono text-xs">// loading…</div>
            ) : users.length === 0 ? (
              <div className="px-3 py-10 text-center text-[#757065] font-mono text-xs uppercase tracking-widest">
                No users yet.
              </div>
            ) : users.map((u) => {
              const meta = ROLE_META[u.role] || ROLE_META.user;
              return (
                <button
                  key={u.user_id}
                  onClick={() => setSelected(u.user_id)}
                  data-testid={`admin-users-row-${u.user_id}`}
                  className="w-full text-left flex flex-wrap items-center gap-3 border-b border-[#2D2A26] last:border-b-0 px-3 py-2 hover:bg-[#0a0907]"
                >
                  {u.is_owner ? (
                    <span
                      className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#E0A526] border border-[#E0A526] px-2 py-0.5 shrink-0 inline-flex items-center gap-1"
                      data-testid={`admin-users-row-owner-${u.user_id}`}
                    >
                      <Crown size={10} /> OWNER
                    </span>
                  ) : (
                    <span
                      style={{ color: meta.color, borderColor: meta.color }}
                      className="font-mono text-[9px] uppercase tracking-[0.3em] border px-2 py-0.5 shrink-0"
                    >
                      {meta.label}
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="font-mono text-sm text-[#E4DFD3] truncate">{u.email || "—"}</div>
                    <div className="font-mono text-[10px] text-[#757065] truncate">
                      {u.name || "—"} · {(u.effective_permissions || []).length} eff. perms
                      {u.suspended ? " · SUSPENDED" : ""}
                    </div>
                  </div>
                  <span className="font-mono text-[9px] text-[#757065] hidden sm:inline">
                    joined {(u.created_at || "").slice(0, 10)}
                  </span>
                </button>
              );
            })}
          </div>
        </>
      )}

      {tab === "audit" && <RbacAuditLog token={token} userToken={userToken} />}

      {selected && (
        <UserDrawer
          token={token}
          userToken={userToken}
          userId={selected}
          isOwnerActor={isOwnerActor}
          onClose={() => setSelected(null)}
          onChanged={load}
        />
      )}
    </div>
  );
};


// ---------------------------------------------------------------------
// Per-user RBAC drawer
// ---------------------------------------------------------------------
const UserDrawer = ({ token, userToken, userId, isOwnerActor, onClose, onChanged }) => {
  const [u, setU] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allPerms, setAllPerms] = useState([]);
  const [templates, setTemplates] = useState({});
  const [roles, setRoles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ur, pr, rr] = await Promise.all([
        adminRbacUsers(token, { q: "", limit: 500 }, userToken),
        adminRbacPermissions(token, userToken),
        adminRbacRoles(token, userToken),
      ]);
      setAllPerms(pr.permissions || []);
      setTemplates(pr.templates || {});
      setRoles(rr.roles || []);
      const row = (ur.rows || []).find((x) => x.user_id === userId);
      setU(row || null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load user");
    } finally { setLoading(false); }
  }, [token, userToken, userId]);

  useEffect(() => { load(); }, [load]);

  const apply = async (patch, msg = "Updated") => {
    if (!isOwnerActor) {
      toast.error("Owner-only action.");
      return;
    }
    if (u?.is_owner) {
      toast.error("The Owner is permanent and cannot be modified.");
      return;
    }
    setBusy(true);
    try {
      const body = { ...patch };
      if (reason.trim()) body.reason = reason.trim();
      await adminRbacPatchUser(token, userId, body, userToken);
      toast.success(msg);
      setReason("");
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  const ownerLocked = !!u?.is_owner;
  const effective = useMemo(() => new Set(u?.effective_permissions || []), [u]);
  const explicit  = useMemo(() => new Set(u?.permissions || []), [u]);
  const revoked   = useMemo(() => new Set(u?.revoked_permissions || []), [u]);

  return (
    <div
      className="fixed inset-0 z-[70] flex items-start justify-end bg-black/70 backdrop-blur-md"
      role="dialog" aria-modal="true"
      data-testid="admin-users-drawer"
    >
      <div className="w-full max-w-xl h-full bg-[#0a0907] border-l border-[#2D2A26] overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-[#2D2A26] bg-[#0a0907] px-3 py-2">
          <button type="button" onClick={onClose} data-testid="admin-users-drawer-close"
            className="inline-flex items-center justify-center w-7 h-7 text-[#A39E93] hover:text-[#D92D20]">
            <ChevronLeft size={14} />
          </button>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20]">
            // USER · RBAC
          </div>
          <button type="button" onClick={onClose}
            className="ml-auto inline-flex items-center justify-center w-7 h-7 text-[#A39E93] hover:text-[#D92D20]"
            aria-label="Close">
            <X size={13} />
          </button>
        </div>

        {loading || !u ? (
          <div className="p-6 font-mono text-xs text-[#757065]">// loading…</div>
        ) : (
          <div className="p-4 space-y-4">
            {/* Identity card */}
            <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3" data-testid="admin-users-drawer-identity">
              <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] mb-2">
                identity
              </div>
              <div className="font-display text-2xl text-[#E4DFD3]">{u.email || "—"}</div>
              <div className="font-mono text-[10px] text-[#A39E93] mt-1 flex flex-wrap items-center gap-2">
                <span>{u.name || "—"}</span>
                {u.is_owner && (
                  <span className="border border-[#E0A526] text-[#E0A526] px-1.5 py-0.5 inline-flex items-center gap-1">
                    <Crown size={10} /> OWNER · PERMANENT
                  </span>
                )}
                {!u.is_owner && u.role && (
                  <span style={{ color: (ROLE_META[u.role]||ROLE_META.user).color, borderColor: (ROLE_META[u.role]||ROLE_META.user).color }}
                    className="border px-1.5 py-0.5 uppercase tracking-[0.25em]">
                    {(ROLE_META[u.role]||ROLE_META.user).label}
                  </span>
                )}
                {u.suspended && (
                  <span className="border border-[#D92D20] text-[#D92D20] px-1.5 py-0.5 uppercase tracking-[0.25em]">
                    SUSPENDED
                  </span>
                )}
              </div>
              <div className="mt-1 font-mono text-[10px] text-[#757065]">
                joined {(u.created_at || "").slice(0, 10)} · last login {(u.last_login_at || "—").slice(0, 16)}
              </div>
            </div>

            {ownerLocked && (
              <div className="border border-[#E0A526]/40 bg-[#1a1408] p-3 flex items-start gap-2" data-testid="admin-users-drawer-owner-locked">
                <Crown size={14} className="text-[#E0A526] mt-0.5" />
                <div className="font-mono text-[11px] text-[#CBB892]">
                  This is the Owner. Owner cannot be deleted, suspended, demoted,
                  or stripped of permissions. Only a future dedicated
                  ownership-transfer workflow may move ownership.
                </div>
              </div>
            )}

            {/* Appoint role */}
            <Section title="Appoint Role">
              <div className="flex flex-wrap gap-2">
                {APPOINT_ROLES.map((r) => {
                  const meta = ROLE_META[r];
                  const active = u.role === r;
                  return (
                    <button
                      key={r}
                      type="button"
                      onClick={() => apply({ role: r }, `Appointed ${meta.label}`)}
                      disabled={ownerLocked || busy || !isOwnerActor}
                      data-testid={`admin-users-appoint-${r}`}
                      style={active ? { color: meta.color, borderColor: meta.color } : undefined}
                      className={
                        "px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border " +
                        (active ? "bg-[#0a0907]" : "border-[#454038] text-[#A39E93] hover:text-[#E4DFD3] hover:border-[#D92D20]") +
                        (ownerLocked || !isOwnerActor ? " opacity-50 cursor-not-allowed" : "")
                      }
                    >
                      Appoint {meta.label}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={() => apply({ role: "subscriber", permissions_clear: u.permissions || [] }, "Removed role")}
                  disabled={ownerLocked || busy || !isOwnerActor || u.role === "subscriber"}
                  data-testid="admin-users-remove-role"
                  className={
                    "px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-[#D92D20] text-[#D92D20] hover:bg-[#1a0a0a] " +
                    (ownerLocked || !isOwnerActor || u.role === "subscriber" ? " opacity-50 cursor-not-allowed" : "")
                  }
                >
                  Remove role
                </button>
              </div>
            </Section>

            {/* Permission templates */}
            <Section title="Permission Templates">
              <div className="flex flex-wrap gap-2">
                {Object.keys(templates).map((k) => (
                  <button
                    key={k}
                    type="button"
                    onClick={() => apply({ template: k }, `Applied ${k}`)}
                    disabled={ownerLocked || busy || !isOwnerActor}
                    data-testid={`admin-users-template-${k}`}
                    className={
                      "px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border border-[#454038] hover:border-[#5DD4B3] text-[#A39E93] hover:text-[#5DD4B3] " +
                      (ownerLocked || !isOwnerActor ? " opacity-50 cursor-not-allowed" : "")
                    }
                  >
                    {k.replace(/_/g, " ")}
                  </button>
                ))}
              </div>
            </Section>

            {/* Per-permission checklist */}
            <Section title={`Permissions (${effective.size} effective)`}>
              <div className="grid sm:grid-cols-2 gap-1.5">
                {allPerms.map((p) => {
                  const isEff = effective.has(p);
                  const isExp = explicit.has(p);
                  const isRev = revoked.has(p);
                  return (
                    <label
                      key={p}
                      className="inline-flex items-center gap-2 font-mono text-[10px] text-[#A39E93] cursor-pointer"
                      data-testid={`admin-users-perm-${p}`}
                    >
                      <input
                        type="checkbox"
                        checked={isEff}
                        disabled={ownerLocked || busy || !isOwnerActor}
                        onChange={(e) => {
                          if (e.target.checked) {
                            apply({ permissions_grant: [p] }, `Granted ${p}`);
                          } else {
                            apply({ permissions_revoke: [p] }, `Revoked ${p}`);
                          }
                        }}
                      />
                      <span className={isEff ? "text-[#E4DFD3]" : ""}>{p}</span>
                      {isExp && <span className="text-[#5DD4B3] text-[8px]">·grant</span>}
                      {isRev && <span className="text-[#D92D20] text-[8px]">·revoked</span>}
                    </label>
                  );
                })}
              </div>
              <div className="mt-2 font-mono text-[8.5px] text-[#5a544a] uppercase tracking-[0.25em]">
                // tick to grant · untick to explicitly revoke even a role default
              </div>
            </Section>

            {/* Suspension */}
            <Section title="Status">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => apply({ suspended: !u.suspended }, u.suspended ? "Reinstated" : "Suspended")}
                  disabled={ownerLocked || busy || !isOwnerActor}
                  data-testid="admin-users-suspend-toggle"
                  className={
                    "inline-flex items-center gap-1.5 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] border " +
                    (u.suspended
                      ? "border-[#5DD4B3] text-[#5DD4B3] hover:bg-[#0c1a0e]"
                      : "border-[#D92D20] text-[#D92D20] hover:bg-[#1a0a0a]") +
                    (ownerLocked || !isOwnerActor ? " opacity-50 cursor-not-allowed" : "")
                  }
                >
                  {u.suspended ? <Check size={11} /> : <AlertTriangle size={11} />}
                  {u.suspended ? "Reinstate user" : "Suspend user"}
                </button>
                <span className="font-mono text-[10px] text-[#757065]">
                  Status: <span className={u.suspended ? "text-[#D92D20]" : "text-[#5DD4B3]"}>
                    {u.suspended ? "SUSPENDED" : "ACTIVE"}
                  </span>
                </span>
              </div>
            </Section>

            {/* Reason (attached to next action) */}
            <Section title="Reason (optional · attached to next change)">
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                maxLength={500}
                placeholder="e.g. promoted to editor — onboarding"
                disabled={ownerLocked || busy || !isOwnerActor}
                data-testid="admin-users-reason"
                className="w-full bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-2 py-1.5 font-mono text-[11px] text-[#E4DFD3]"
              />
            </Section>

            {busy && (
              <div className="font-mono text-[10px] text-[#A39E93] inline-flex items-center gap-2">
                <Loader2 size={11} className="animate-spin" /> applying…
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};


const Section = ({ title, children }) => (
  <div className="border border-[#2D2A26] bg-[#0F0F0F] p-3">
    <div className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#A39E93] mb-2">
      {title}
    </div>
    {children}
  </div>
);


// ---------------------------------------------------------------------
// RBAC Audit Log tab
// ---------------------------------------------------------------------
const RbacAuditLog = ({ token, userToken }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminRbacAuditLog(token, 100, userToken);
      setRows(r.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit log");
    } finally { setLoading(false); }
  }, [token, userToken]);
  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-users-audit-log">
      <div className="flex items-center gap-2 mb-3">
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="admin-users-audit-refresh"
          className="inline-flex items-center gap-1.5 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]"
        >
          <RefreshCw size={11} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
        <div className="font-mono text-[10px] text-[#757065]">
          <HistoryIcon size={10} className="inline mr-1" /> RBAC mutations (latest 100)
        </div>
      </div>
      <div className="border border-[#2D2A26] bg-[#131313]">
        {rows.length === 0 ? (
          <div className="px-3 py-10 text-center text-[#757065] font-mono text-xs">
            // no audit entries yet
          </div>
        ) : rows.map((r, i) => (
          <div key={i} className="border-b border-[#2D2A26] last:border-b-0 px-3 py-2 font-mono text-[10.5px] text-[#A39E93]" data-testid={`admin-users-audit-row-${i}`}>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-[#D92D20] uppercase tracking-[0.25em] text-[9px]">{r.event}</span>
              <span className="text-[#E4DFD3]">{r.actor_email || "operator-token"}</span>
              <span className="text-[#757065]">→ {r.target_email || r.target_user_id || "—"}</span>
              <span className="ml-auto text-[#5a544a] text-[9px]">{(r.ts || "").slice(0, 19).replace("T", " ")}</span>
            </div>
            {(r.old_role || r.new_role) && (
              <div className="mt-0.5 text-[#CBB892] text-[10px]">
                role: {r.old_role || "—"} → {r.new_role || "—"}
              </div>
            )}
            {r.reason && <div className="mt-0.5 text-[#5DD4B3] text-[10px]">// {r.reason}</div>}
          </div>
        ))}
      </div>
    </div>
  );
};


export default AdminUsersPanel;
